// Inventering in the administration (mockup counting-v1 §3, 3a): plan a round as a selection in
// the location tree (an aisle, a shelf, four aisles, a zone), follow its progress, read the
// deviations with what they mean, and approve — which turns them into Adjusted events.
import { useEffect, useMemo, useState } from "react";
import { printAndTell, Api, type BundleLocation, type CountRound, type CountRoundDetail, type ReferenceBundle } from "../api/client.js";
import { fmtQty, t } from "../i18n.js";
import { DetailBar, useDetailHistory } from "./settings-ui.jsx";

export function Counting() {
  const [rounds, setRounds] = useState<CountRound[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<CountRoundDetail | null>(null);
  useDetailHistory(!!(detail || selected), () => { setSelected(null); setDetail(null); });
  const [planning, setPlanning] = useState(false);
  const [bundle, setBundle] = useState<ReferenceBundle | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [name, setName] = useState("");
  const [rule, setRule] = useState<"" | "none" | "split" | "all">("");
  const [msg, setMsg] = useState<string | null>(null);
  const [locks, setLocks] = useState<{ locationId: string; code: string; heldBy: string; since: string }[]>([]);
  const [last, setLast] = useState<Map<string, string>>(new Map());
  const [tick, setTick] = useState(0);

  const load = () => {
    Api.countRounds("open,review,approved,cancelled").then(setRounds).catch(() => setRounds([]));
    Api.countLocks().then(setLocks).catch(() => setLocks([]));
    Api.countLast().then((r) => setLast(new Map(r.map((x) => [x.locationId, x.at])))).catch(() => undefined);
  };
  useEffect(load, [tick]);
  useEffect(() => { Api.bundle().then(setBundle).catch(() => setBundle(null)); }, []);
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    Api.countRound(selected).then(setDetail).catch(() => setDetail(null));
  }, [selected, tick]);
  // Follow a running round: refresh every 20 s.
  useEffect(() => { const h = setInterval(() => setTick((n) => n + 1), 20_000); return () => clearInterval(h); }, []);

  // The tree: zones → aisles → racks → bins, with how many bins and containers each part holds.
  const kids = useMemo(() => {
    const m = new Map<string, BundleLocation[]>();
    for (const l of bundle?.locations ?? []) { const parent = l.path.includes("/") ? l.path.slice(0, l.path.lastIndexOf("/")) : ""; m.set(parent, [...(m.get(parent) ?? []), l]); }
    for (const v of m.values()) v.sort((a, b) => a.code.localeCompare(b.code, "sv", { numeric: true }));
    return m;
  }, [bundle]);
  const binsUnder = (l: BundleLocation) => (bundle?.locations ?? []).filter((x) => (x.kind === "bin" || x.kind === "staging") && (x.path === l.path || x.path.startsWith(`${l.path}/`))).length;
  const selectedBins = useMemo(() => {
    const set = new Set<string>();
    for (const id of picked) { const l = bundle?.locations.find((x) => x.id === id); if (!l) continue; for (const x of bundle?.locations ?? []) if ((x.kind === "bin" || x.kind === "staging") && (x.path === l.path || x.path.startsWith(`${l.path}/`))) set.add(x.id); }
    return set.size;
  }, [picked, bundle]);
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const [open, setOpen] = useState<Set<string>>(new Set());
  // When the bins under a node were last counted: the bin's own date, or for a group how many were
  // never counted and the oldest date — what the planner picks rounds by (counting step 3).
  const lastText = (l: BundleLocation) => {
    const fmtD = (iso: string) => new Date(iso).toLocaleDateString("sv-SE");
    if (l.kind === "bin" || l.kind === "staging") { const at = last.get(l.id); return at ? t("adm.cnt.last", { at: fmtD(at) }) : t("adm.cnt.never"); }
    const under = (bundle?.locations ?? []).filter((x) => (x.kind === "bin" || x.kind === "staging") && (x.path === l.path || x.path.startsWith(`${l.path}/`)));
    const dates = under.map((x) => last.get(x.id)).filter((x): x is string => !!x).sort();
    const never = under.length - dates.length;
    return [never > 0 ? t("adm.cnt.neverN", { n: never }) : null, dates[0] ? t("adm.cnt.oldest", { at: fmtD(dates[0]) }) : null].filter(Boolean).join(" · ") || t("adm.cnt.never");
  };
  const Node = ({ l, depth }: { l: BundleLocation; depth: number }) => {
    const ch = (kids.get(l.path) ?? []).filter((x) => x.kind !== "bin" || depth < 3);
    const isLeaf = (kids.get(l.path) ?? []).length === 0;
    const bins = binsUnder(l);
    if (l.kind === "virtual") return null;
    return (
      <div style={{ paddingLeft: depth * 16 }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center", padding: "3px 0", cursor: "pointer" }}>
          {!isLeaf ? <button type="button" className="abtn sm" style={{ width: 26, padding: 0 }} onClick={(e) => { e.preventDefault(); setOpen((s) => { const n = new Set(s); if (n.has(l.id)) n.delete(l.id); else n.add(l.id); return n; }); }}>{open.has(l.id) ? "–" : "+"}</button> : <span style={{ width: 26 }} />}
          <input type="checkbox" checked={picked.has(l.id)} onChange={() => toggle(l.id)} />
          <b>{l.code}</b> <span className="mut">{l.kind} · {t("adm.cnt.bins", { n: bins })} · {lastText(l)}</span>
        </label>
        {open.has(l.id) && (kids.get(l.path) ?? []).map((c) => <Node key={c.id} l={c} depth={depth + 1} />)}
        {open.has(l.id) && ch.length === 0 && null}
      </div>
    );
  };
  const roots = (kids.get("") ?? []).filter((l) => l.kind !== "virtual");

  const start = async () => {
    setMsg(null);
    try {
      const r = await Api.createCountRound({ name: name.trim() || t("adm.cnt.defaultName", { date: new Date().toISOString().slice(0, 10) }), locationIds: [...picked], quantityRule: rule || null });
      setPlanning(false); setPicked(new Set()); setName(""); setRule("");
      setTick((n) => n + 1); setSelected(r.id);
    } catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
  };
  const approve = async () => {
    if (!detail) return;
    if (!window.confirm(t("adm.cnt.approveConfirm", { n: detail.deviations.length }))) return;
    try { const r = await Api.approveCountRound(detail.id); setMsg(t("adm.cnt.approved", { n: r.adjusted })); setTick((n) => n + 1); } catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
  };
  const statusPill = (s: string) => <span className={`pill ${s === "approved" ? "ok" : s === "review" ? "warn" : s === "cancelled" ? "mut" : ""}`}>{t(`adm.cnt.status.${s}` as "adm.cnt.status.open")}</span>;

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.counting")}</h1><div className="crumb">{t("adm.cnt.sub")}</div></div>
        <div className="right"><button type="button" className="abtn primary" onClick={() => setPlanning(true)} disabled={!bundle}>{t("adm.cnt.new")}</button></div>
      </div>
      <div className="adm-content">
        {msg && <p className="ahint" style={{ marginBottom: 10 }}>{msg}</p>}
        {planning && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="hd">{t("adm.cnt.plan")} <span className="sub">{t("adm.cnt.plan.sub")}</span></div>
            <div className="bd" style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16 }}>
              <div style={{ maxHeight: 420, overflowY: "auto", fontSize: 14 }}>{roots.map((l) => <Node key={l.id} l={l} depth={0} />)}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div className="ain" style={{ height: "auto", padding: 10 }}><div className="mut" style={{ fontSize: 12, textTransform: "uppercase" }}>{t("adm.cnt.selected")}</div><div style={{ fontSize: 22, fontWeight: 700 }}>{t("adm.cnt.bins", { n: selectedBins })}</div></div>
                <label className="mut" style={{ fontSize: 13 }}>{t("adm.cnt.name")}<input className="ain" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("adm.cnt.defaultName", { date: new Date().toISOString().slice(0, 10) })} /></label>
                <label className="mut" style={{ fontSize: 13 }}>{t("adm.cnt.rule")}
                  <select value={rule} onChange={(e) => setRule(e.target.value as typeof rule)}>
                    <option value="">{t("adm.cnt.rule.site")}</option><option value="none">{t("adm.set.counting.quantity.none")}</option><option value="split">{t("adm.set.counting.quantity.split")}</option><option value="all">{t("adm.set.counting.quantity.all")}</option>
                  </select>
                </label>
                <p className="ahint" style={{ margin: 0 }}>{t("adm.cnt.plan.hint")}</p>
                <div style={{ display: "flex", gap: 8 }}><button type="button" className="abtn primary" disabled={selectedBins === 0} onClick={() => void start()}>{t("adm.cnt.start")}</button><button type="button" className="abtn" onClick={() => setPlanning(false)}>{t("common.cancel")}</button></div>
              </div>
            </div>
          </div>
        )}
        <div className={`adm-cols${!!(detail || selected) ? " detail-open" : ""}`}>
          <div className="card adm-w460">
            <div className="bd flush">
              <table className="at">
                <thead><tr><th>{t("adm.cnt.round")}</th><th>{t("adm.cnt.progress")}</th><th>{t("adm.col.status")}</th></tr></thead>
                <tbody>
                  {rounds?.length === 0 && <tr><td colSpan={3} className="mut">{t("adm.cnt.none")}</td></tr>}
                  {rounds?.filter((r) => !((bundle?.sites.length ?? 0) > 1 && bundle?.siteId) || r.siteId === bundle.siteId).map((r) => (
                    <tr key={r.id} className={`click${selected === r.id ? " sel" : ""}`} onClick={() => setSelected(r.id)}>
                      <td><b>{r.name}</b><div className="mut" style={{ fontSize: 12 }}>{new Date(r.createdAt).toLocaleDateString("sv-SE")} · {r.createdBy ?? ""}</div></td>
                      <td>{r.counted}/{r.locations}{r.missing ? <span className="mut"> · {t("adm.cnt.missingN", { n: r.missing })}</span> : null}</td>
                      <td>{statusPill(r.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {locks.length > 0 && (
              <div className="bd" style={{ borderTop: "1px solid var(--line)" }}>
                <div className="mut" style={{ fontSize: 12, textTransform: "uppercase", marginBottom: 4 }}>{t("adm.cnt.locks")}</div>
                {locks.map((k) => <div key={k.locationId} style={{ fontSize: 13 }}><b>{k.code}</b> · {k.heldBy} · {new Date(k.since).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}</div>)}
              </div>
            )}
          </div>
          <div className="card adm-grow adm-detail">
            {!!(detail || selected) && <DetailBar title={detail ? detail.name : t("common.loading")} sub={undefined} onBack={() => { setSelected(null); setDetail(null); }} />}
            {!detail && <div className="bd"><p className="ahint">{t("adm.cnt.pick")}</p></div>}
            {detail && (
              <>
                <div className="hd">{detail.name} <span className="sub">{t("adm.cnt.detail.sub", { done: detail.counted, total: detail.locations, by: detail.createdBy ?? "" })}</span></div>
                <div className="bd" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                  {[["adm.cnt.k.done", `${detail.counted} / ${detail.locations}`], ["adm.cnt.k.present", String(detail.present)], ["adm.cnt.k.missing", String(detail.missing)], ["adm.cnt.k.found", String(detail.found)]].map(([k, v]) => (
                    <div key={k} className="ain" style={{ height: "auto", padding: 10 }}><div className="mut" style={{ fontSize: 12, textTransform: "uppercase" }}>{t(k as "adm.cnt.k.done")}</div><div style={{ fontSize: 24, fontWeight: 700 }}>{v}</div></div>
                  ))}
                </div>
                <div className="bd flush">
                  <table className="at">
                    <thead><tr><th>{t("adm.cnt.dev")}</th><th>{t("adm.cnt.container")}</th><th>{t("pick.location")}</th><th>{t("adm.cnt.means")}</th></tr></thead>
                    <tbody>
                      {detail.deviations.length === 0 && <tr><td colSpan={4} className="mut">{detail.counted === 0 ? t("adm.cnt.noneYet") : t("adm.cnt.noDev")}</td></tr>}
                      {detail.deviations.map((d) => (
                        <tr key={`${d.kind}-${d.containerId}`}>
                          <td>{d.kind === "missing" ? t("adm.cnt.dev.missing") : t("adm.cnt.dev.quantity")}</td>
                          <td><b>{d.sku ?? d.gtin ?? "?"}</b>{d.lot ? <span className="id"> · {d.lot}</span> : null}</td>
                          <td className="id">{d.locationCode}</td>
                          <td>{d.kind === "missing" ? `−${fmtQty(d.trail.value, d.trail.uom)}` : `${fmtQty(d.trail.value, d.trail.uom)} → ${fmtQty(d.counted?.value ?? "0", d.counted?.uom ?? d.trail.uom)}`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bd flush">
                  <table className="at">
                    <thead><tr><th>{t("pick.location")}</th><th>{t("adm.col.status")}</th><th>{t("adm.cnt.counts")}</th><th>{t("adm.cnt.who")}</th></tr></thead>
                    <tbody>
                      {detail.locationList.map((l) => (
                        <tr key={l.locationId}>
                          <td className="id">{l.code}</td>
                          <td>{l.state === "counted" ? <span className="pill ok">{t("adm.cnt.loc.counted")}</span> : l.state === "counting" ? <span className="pill warn">{t("adm.cnt.loc.counting")}</span> : <span className="pill mut">{t("adm.cnt.loc.pending")}</span>}</td>
                          <td className="mut">{l.state === "counted" ? t("adm.cnt.loc.counts", { present: l.present ?? 0, missing: l.missing ?? 0, found: l.found ?? 0 }) : ""}</td>
                          <td className="mut">{l.heldBy ?? l.countedBy ?? ""}{l.countedAt ? ` · ${new Date(l.countedAt).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}` : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(detail.status === "open" || detail.status === "review") && (
                  <div className="bd" style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <button type="button" className="abtn" title={t("adm.print.viaAgent")} onClick={() => void printAndTell({ kind: "count-sheet", roundId: detail.id }, t as never)}>{t("adm.cnt.printSheet")}</button>
                    <button type="button" className="abtn primary" onClick={() => void approve()}>{t("adm.cnt.approve", { n: detail.deviations.length })}</button>
                    <button type="button" className="abtn danger" onClick={() => { if (window.confirm(t("adm.cnt.cancelConfirm"))) void Api.cancelCountRound(detail.id).then(() => setTick((n) => n + 1)); }}>{t("adm.cnt.cancel")}</button>
                    <span className="ahint" style={{ margin: 0 }}>{detail.counted < detail.locations ? t("adm.cnt.approve.partial") : t("adm.cnt.approve.hint")}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
